import { Icon } from '@design/components/icon/Icon';
import { SvgIconNames } from '@design/design-tokens/iconography/SvgIcons';
import clsx from 'clsx';
import React, { HTMLAttributes, ReactNode, Ref } from 'react';

import styles from './Badge.module.scss';

export type BadgeSize = 'xsmall' | 'small' | 'medium';
export type BadgeStyle = 'solid' | 'outline';
export type BadgeVariant = 'textual' | 'numerical';
export type BadgeColor =
  | 'neutral'
  | 'primary'
  | 'error'
  | 'success'
  | 'warning';

export interface BadgeProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Layout: `'textual'` renders pill chrome with optional icons and
   * left section, `'numerical'` renders just the label as a count
   * pill (icons and `leftSection` are ignored). Defaults to `'textual'`.
   */
  variant?: BadgeVariant;
  /** Size preset. Defaults to `'medium'`. */
  size?: BadgeSize;
  /** Color treatment. Defaults to `'primary'`. Has no effect on `variant="numerical"`. */
  color?: BadgeColor;
  /** Visual style: `'solid'` (default, filled) or `'outline'` (border only). Has no effect on `variant="numerical"`. */
  badgeStyle?: BadgeStyle;
  /** Badge content. */
  label?: ReactNode;
  /** Optional icon rendered before the label. Textual variant only. */
  leftIcon?: SvgIconNames;
  /** Optional icon rendered after the label. Textual variant only. */
  rightIcon?: SvgIconNames;
  /** Arbitrary content rendered before the icon and label (e.g. an avatar). Textual variant only. */
  leftSection?: React.ReactNode;
  /** Forwarded to the outer `<div>`. */
  ref?: Ref<HTMLDivElement>;
}

const textualStyleMap: Record<BadgeStyle, Record<BadgeColor, string>> = {
  solid: {
    neutral: styles.solidNeutral,
    primary: styles.solidPrimary,
    success: styles.solidSuccess,
    warning: styles.solidWarning,
    error: styles.solidError,
  },
  outline: {
    neutral: styles.outlineNeutral,
    primary: styles.outlinePrimary,
    success: styles.outlineSuccess,
    warning: styles.outlineWarning,
    error: styles.outlineError,
  },
};

/**
 * Compact pill that surfaces status, count, or category metadata
 * alongside other content.
 *
 * Two layouts:
 *
 * - `variant="textual"` (default) renders a label with optional
 *   `leftSection`, `leftIcon`, and `rightIcon`, styled by `color`,
 *   `badgeStyle`, and `size`.
 * - `variant="numerical"` renders just the label inside a tight count
 *   pill — icons, color, and `badgeStyle` are ignored.
 */
export const Badge = ({
  color = 'primary',
  size = 'medium',
  badgeStyle = 'solid',
  variant = 'textual',
  label,
  leftIcon,
  rightIcon,
  leftSection,
  className,
  ref,
  ...rest
}: BadgeProps) => {
  const mapSizeToClass = {
    xsmall: styles.sizeXsSmall,
    small: styles.sizeSmall,
    medium: styles.sizeMedium,
  };
  const rootClass = clsx(
    styles.root,
    size === 'small' && styles.sizeSmall,
    className
  );

  if (variant === 'numerical') {
    return (
      <div
        ref={ref}
        data-testid="badge-numerical"
        className={rootClass}
        {...rest}
      >
        <p
          data-badge="numerical"
          className={clsx(styles.numerical, mapSizeToClass[size])}
        >
          {label}
        </p>
      </div>
    );
  }

  const iconSize = size === 'small' ? 12 : 16;

  return (
    <div ref={ref} data-testid="badge-textual" className={rootClass} {...rest}>
      <div
        data-badge="textual"
        className={clsx(
          styles.textual,
          mapSizeToClass[size],
          textualStyleMap[badgeStyle][color]
        )}
      >
        {leftSection}
        {leftIcon && <Icon iconName={leftIcon} size={iconSize} />}
        <span>{label}</span>
        {rightIcon && <Icon iconName={rightIcon} size={iconSize} />}
      </div>
    </div>
  );
};
